import { Link } from "react-router-dom";
import { SmartLink } from "@/components/CompanyUi";
import { aiProducts } from "@/lib/home-content";

const STATUS = {
    live: {
        label: "Live",
        dot: "bg-[#3d8f62]",
        text: "text-[#3d8f62]",
    },
    beta: {
        label: "Beta",
        dot: "bg-[#7aa2ff]",
        text: "text-[#7aa2ff]",
    },
};

const GROUPS = [
    { status: "live", label: "Live products" },
    { status: "beta", label: "Beta products" },
];

const PORTFOLIO_ORDER = [
    "Navlyt",
    "AgriIntel",
    "CovioIQ",
    "PetVitale",
    "BuildersBridge",
    "HVAC Recovery",
    "CellarHost",
    "RealtorDesk AI",
    "Canada Compliance AI",
];

function byPortfolioOrder(a, b) {
    return PORTFOLIO_ORDER.indexOf(a.name) - PORTFOLIO_ORDER.indexOf(b.name);
}

function ProductRow({ product, flip }) {
    const tone = STATUS[product.status] || STATUS.live;
    const line = product.line || product.category;
    const body = product.detail || product.description;

    return (
        <article className="grid items-center gap-8 rounded-2xl bg-[#f6f7fb] p-6 ring-1 ring-black/5 md:p-8 lg:grid-cols-2 lg:gap-12">
            <div className={`relative overflow-hidden rounded-xl bg-[#0a0a0b] ring-1 ring-black/5 ${flip ? "lg:order-2" : ""}`}>
                <div className="relative aspect-video w-full">
                    {product.image ? (
                        <img
                            src={product.image}
                            alt={product.imageAlt || ""}
                            className="h-full w-full object-cover object-top"
                        />
                    ) : (
                        <div className="flex h-full flex-col justify-end bg-[#0a0a0b] p-6 text-white">
                            <span className="font-display text-3xl font-extrabold">{product.name}</span>
                        </div>
                    )}
                </div>
                <div className="absolute left-3 top-3">
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${tone.text}`}
                    >
                        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                        {tone.label}
                    </span>
                </div>
            </div>
            <div className={`flex flex-col gap-4 ${flip ? "lg:order-1" : ""}`}>
                <div>
                    <h3 className="font-display text-3xl font-extrabold tracking-tight text-[#0a0a0b] md:text-4xl">
                        {product.name}
                    </h3>
                    <p className="mt-2 text-base text-[#5b5b66] md:text-lg">{line}</p>
                </div>
                <p className="text-sm leading-relaxed text-[#5b5b66] md:text-base">{body}</p>
                {product.stack?.length ? (
                    <ul className="flex flex-wrap gap-2" aria-label={`${product.name} tech stack`}>
                        {product.stack.map((item) => (
                            <li
                                key={item}
                                className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[#5b5b66] ring-1 ring-black/10"
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                ) : null}
                <div className="mt-1 flex flex-wrap items-center gap-3">
                    <SmartLink
                        href={product.href}
                        aria-label={`View ${product.name} project`}
                        className="inline-flex items-center rounded-full bg-[#0a0a0b] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                    >
                        View Project
                    </SmartLink>
                    <SmartLink
                        href={product.href}
                        aria-label={`Visit ${product.name}`}
                        className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0a0a0b] ring-1 ring-black/10 transition-colors hover:text-[var(--brand)]"
                    >
                        Visit
                    </SmartLink>
                </div>
            </div>
        </article>
    );
}

export default function ProductsPage() {
    return (
        <main data-testid="products-page" className="bg-white text-[#0a0a0b]">
            <section className="bg-lavender px-5 pb-16 pt-36 sm:pb-20 sm:pt-44">
                <div className="mx-auto max-w-5xl">
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#5b5b66] ring-1 ring-black/5">
                        Portfolio
                    </span>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.02] tracking-tight sm:text-7xl">
                        Vertical AI SaaS <span className="text-gradient">Products</span>
                    </h1>
                    <p className="mt-5 text-lg text-[#5b5b66]">by SalesCan</p>
                    <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#5b5b66] sm:text-lg">
                        Software we design, build, and operate. Real estate, compliance, insurance, pet care,
                        agriculture, HVAC, aviation, construction, and wineries.
                    </p>
                </div>
            </section>

            <section className="px-5 py-14 sm:py-20">
                <div className="mx-auto flex max-w-6xl flex-col gap-16">
                    {GROUPS.map((group) => {
                        const items = aiProducts.items.filter((item) => item.status === group.status).sort(byPortfolioOrder);
                        const tone = STATUS[group.status];
                        const offset = group.status === "beta" ? aiProducts.items.filter((item) => item.status === "live").length : 0;
                        return (
                            <div key={group.status}>
                                <div className="mb-8 flex items-center gap-3 md:mb-12">
                                    <span className={`h-2 w-2 rounded-full ${tone.dot}`} aria-hidden="true" />
                                    <h2 className={`text-xs font-semibold uppercase tracking-[0.25em] ${tone.text}`}>
                                        {group.label}
                                    </h2>
                                    <span className="text-xs text-[#8a8a95]">({items.length})</span>
                                    <div className="ml-2 h-px flex-1 bg-gradient-to-r from-black/10 to-transparent" />
                                </div>
                                <div className="flex flex-col gap-5">
                                    {items.map((product, index) => (
                                        <ProductRow key={product.name} product={product} flip={(offset + index) % 2 === 1} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="px-5 pb-20">
                <div className="mx-auto max-w-6xl rounded-3xl bg-[#f6f7fb] px-6 py-14 text-center ring-1 ring-black/5 sm:px-12">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand)]">Start a project</p>
                    <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                        Tell us what you want built.
                    </h2>
                    <p className="mx-auto mt-3 max-w-xl text-[#5b5b66]">
                        A website, a product, or an automation. We reply from St. Catharines.
                    </p>
                    <Link
                        to="/#contact"
                        className="mt-8 inline-flex rounded-full bg-[#0a0a0b] px-6 py-3 text-sm font-semibold text-white"
                    >
                        Start a project
                    </Link>
                </div>
            </section>
        </main>
    );
}
